import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import { Loader2, Save, Edit, Ban, Trash2, Key, Wallet, DollarSign, AlertTriangle } from 'lucide-react';
import { PhoneInput } from '../PhoneInput';

const BAN_REASONS = [
  'Duplicate Person', 'Abuse', 'Fraud', 'Scam', 'Spam',
  'Terms Violation', 'Suspicious Activity', 'Identity Theft',
  'Money Laundering', 'Harassment', 'Chargebacks', 'Other'
];

const DELETE_REASONS = [
  'Duplicate Person', 'Abuse', 'Fraud', 'Scam',
  'User Request', 'Inactive Account', 'Terms Violation', 'Other'
];

export function UserEditDialog({ open, onOpenChange, user, form, setForm, priorDeletions, onSave, loading }) {
  const [plans, setPlans] = React.useState([]);
  React.useEffect(() => {
    if (!open) return;
    // Pull active plans from the public endpoint so the admin can pick a
    // valid tier from the same list members see.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/plans`)
      .then(r => r.json())
      .then(d => setPlans((d.plans || []).filter(p => p.is_active !== false)))
      .catch(() => {});
  }, [open]);

  const formatDeletedAt = (iso) => {
    if (!iso) return 'unknown date';
    try {
      return new Date(iso).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Edit className="w-5 h-5 text-[#00E5FF]" /> Edit User — {user?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {priorDeletions && priorDeletions.length > 0 && (
            <div className="p-3 rounded-lg bg-[#FFEA00]/5 border border-[#FFEA00]/30" data-testid="prior-deletions-panel">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#FFEA00] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[#FFEA00] text-xs font-medium mb-1.5">
                    Re-registered Account ({priorDeletions.length} prior deletion{priorDeletions.length > 1 ? 's' : ''})
                  </p>
                  <ul className="space-y-1">
                    {priorDeletions.map((d, i) => (
                      <li key={d.prior_user_id || `prior-${d.deleted_at}-${i}`} className="text-[#A0B4CC] text-xs" data-testid={`prior-deletion-${i}`}>
                        <span className="text-white">Deleted</span> {formatDeletedAt(d.deleted_at)} <span className="text-[#5C7599]">·</span> <span className="capitalize">{d.role || 'member'}</span>{d.reason && <> <span className="text-[#5C7599]">·</span> <span className="italic">{d.reason}</span></>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[#A0B4CC] text-xs">Name</Label>
              <Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#000B18] border-white/20 text-white" data-testid="edit-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A0B4CC] text-xs">Email</Label>
              <Input value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#000B18] border-white/20 text-white" data-testid="edit-email" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A0B4CC] text-xs">Mobile</Label>
            <PhoneInput
              value={form.mobile || ''}
              onChange={(v) => setForm({ ...form, mobile: v })}
              data-testid="edit-mobile"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[#A0B4CC] text-xs">Role</Label>
              <select value={form.role || 'member'} onChange={e => setForm({...form, role: e.target.value})} className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="edit-role">
                <option value="member">Member</option>
                <option value="merchant">Merchant</option>
                <option value="mro">Merchant Relations Officer</option>
                <option value="moderator">Moderator</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A0B4CC] text-xs">Subscription Status</Label>
              <select value={form.subscription_status || 'inactive'} onChange={e => setForm({...form, subscription_status: e.target.value})} className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="edit-sub-status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A0B4CC] text-xs">Subscription Plan</Label>
            <select
              value={form.subscription_plan || ''}
              onChange={e => setForm({...form, subscription_plan: e.target.value || null})}
              className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm"
              data-testid="edit-sub-plan"
            >
              <option value="">— None —</option>
              {plans.map(p => (
                <option key={p.id} value={p.name}>
                  {p.name} (₱{(p.price || 0).toLocaleString()})
                </option>
              ))}
            </select>
            <p className="text-[#5C7599] text-[11px]">
              Setting a plan + status &quot;Active&quot; comp-subscribes the user (no payment is collected).
              They&apos;ll get all subscriber benefits (wallet, referrals, stock cert tier cap).
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onSave} disabled={loading} className="btn-primary" data-testid="save-edit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UserBanDialog({ open, onOpenChange, user, form, setForm, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#FFEA00] flex items-center gap-2"><Ban className="w-5 h-5" /> Ban User — {user?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-[#FFEA00]/5 border border-[#FFEA00]/20">
            <p className="text-[#FFEA00] text-xs">This will immediately log the user out, prevent future logins, and reject all pending withdrawals.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white">Reason *</Label>
            <select value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="ban-reason">
              <option value="">Select reason...</option>
              {BAN_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A0B4CC] text-xs">Notes (optional)</Label>
            <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-[#000B18] border-white/20 text-white" rows={2} placeholder="Additional context..." data-testid="ban-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onConfirm} disabled={loading || !form.reason} className="bg-[#FFEA00] text-[#000B18] hover:bg-[#FFEA00]/90" data-testid="confirm-ban-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ban className="w-4 h-4 mr-1" /> Confirm Ban</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UserDeleteDialog({ open, onOpenChange, user, form, setForm, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#FF1744] flex items-center gap-2"><Trash2 className="w-5 h-5" /> Delete User — {user?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-[#FF1744]/5 border border-[#FF1744]/20">
            <p className="text-[#FF1744] text-xs">This will soft-delete the account. The user will be hidden from lists and unable to log in. Data is preserved for records.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white">Reason *</Label>
            <select value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="delete-reason">
              <option value="">Select reason...</option>
              {DELETE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onConfirm} disabled={loading || !form.reason} className="bg-[#FF1744] text-white hover:bg-[#FF1744]/90" data-testid="confirm-delete-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1" /> Confirm Delete</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UserPasswordDialog({ open, onOpenChange, user, form, setForm, onSave, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Key className="w-5 h-5 text-[#007AFF]" /> Change Password — {user?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white">New Password</Label>
            <Input type="password" value={form.new_password} onChange={e => setForm({...form, new_password: e.target.value})} className="bg-[#000B18] border-white/20 text-white" placeholder="Min 6 characters" data-testid="new-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white">Confirm Password</Label>
            <Input type="password" value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})} className="bg-[#000B18] border-white/20 text-white" placeholder="Confirm password" data-testid="confirm-password-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onSave} disabled={loading} className="btn-primary" data-testid="save-password-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Key className="w-4 h-4 mr-1" /> Change Password</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UserWalletAdjustDialog({ open, onOpenChange, user, form, setForm, onConfirm, loading }) {
  const cb = form.currentBalance;
  const amt = parseFloat(form.amount);
  const showPreview = cb !== null && cb !== undefined && form.amount && !isNaN(amt);
  const ending = showPreview ? cb + amt : 0;
  const wouldBeNegative = showPreview && ending < 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Wallet className="w-5 h-5 text-[#00E676]" /> Adjust Wallet — {user?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-[#000B18] border border-white/10">
            <p className="text-[#A0B4CC] text-xs mb-1">Current Balance</p>
            <p className="text-white text-2xl font-bold font-outfit" data-testid="wallet-current-balance">
              {cb !== null && cb !== undefined
                ? `PHP ${Number(cb).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : <Loader2 className="w-5 h-5 animate-spin text-[#00E5FF] inline" />}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-[#00E676]/5 border border-[#00E676]/20">
            <p className="text-[#00E676] text-xs">Use positive values to credit and negative values to debit. A valid passkey is required.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white">Amount (PHP)</Label>
            <Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="bg-[#000B18] border-white/20 text-white" placeholder="e.g. 500 or -200" data-testid="wallet-amount" />
          </div>
          {showPreview && (
            <div className="p-3 rounded-lg border" style={{
              backgroundColor: wouldBeNegative ? 'rgba(255,23,68,0.05)' : 'rgba(0,229,255,0.05)',
              borderColor: wouldBeNegative ? 'rgba(255,23,68,0.2)' : 'rgba(0,229,255,0.2)'
            }}>
              <div className="flex items-center justify-between">
                <p className="text-[#A0B4CC] text-xs">Ending Balance</p>
                {wouldBeNegative && (
                  <span className="text-[#FF1744] text-[10px] font-medium">Cannot go negative</span>
                )}
              </div>
              <p className={`text-lg font-bold font-outfit ${wouldBeNegative ? 'text-[#FF1744]' : 'text-white'}`} data-testid="wallet-ending-balance">
                PHP {ending.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-[#A0B4CC] text-xs">Note</Label>
            <Input value={form.note} onChange={e => setForm({...form, note: e.target.value})} className="bg-[#000B18] border-white/20 text-white" placeholder="Reason for adjustment..." data-testid="wallet-note" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white">Passkey *</Label>
            <Input type="password" value={form.passkey} onChange={e => setForm({...form, passkey: e.target.value})} className="bg-[#000B18] border-white/20 text-white" placeholder="Enter admin passkey" data-testid="wallet-passkey" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={onConfirm} disabled={loading || wouldBeNegative} className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90" data-testid="confirm-wallet-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-1" /> Adjust Balance</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
